/**
 * Toast — how a success is said (see DESIGN-SYSTEM.md "Toasts"), an Astryx toast
 * behind a one-argument call.
 *
 * Success only. An error stays where it happened (DESIGN-SYSTEM.md "Error
 * placement"), so there is no error form of this to reach for. The message
 * is also its id: one success said twice in quick succession — a StrictMode
 * double effect, a double submit — is one toast, not two.
 *
 * `ToastLayer` mounts once at the root, above the router, so a toast said
 * just before a navigation is still on screen after it.
 */
import { LayerProvider } from "@astryxdesign/core/Layer";
import { useToast as useAstryxToast } from "@astryxdesign/core/Toast";
import { useCallback, type ReactNode } from "react";

/** Long enough to read a two-line sentence; hover or focus holds it. */
const AUTO_HIDE_MS = 6000;

export function ToastLayer({ children }: { children: ReactNode }) {
  return (
    <LayerProvider toast={{ position: "bottomEnd", maxVisible: 3 }}>
      {children}
    </LayerProvider>
  );
}

export function useSuccessToast(): (message: string) => void {
  const show = useAstryxToast();
  return useCallback(
    (message: string) => {
      show({
        body: message,
        uniqueID: message,
        autoHideDuration: AUTO_HIDE_MS,
      });
    },
    [show],
  );
}
